import { ApiConfig, handleResponse } from './config';
import type { Rating, RatingSubmitData, RatingListResponse, RatingTargetType } from '../../types/rating';

export class RatingService {
  constructor(private config: ApiConfig) {}

  private getAuthHeaders(): Record<string, string> {
    const headers = { ...this.config.headers };

    if (typeof window !== 'undefined') {
      try {
        const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = authStorage.state?.token;
        if (token && token !== 'null' && token !== 'undefined' && token !== '') {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Keep existing headers
      }
    }

    return headers;
  }

  async getRatings(
    targetType: RatingTargetType,
    targetId: string,
    page = 1,
    limit = 10
  ): Promise<RatingListResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetch(
      `${this.config.baseUrl}/ratings/${targetType}/${targetId}?${params}`,
      { headers: this.config.headers }
    );
    return handleResponse(response);
  }

  async getUserRating(targetType: RatingTargetType, targetId: string): Promise<Rating | null> {
    const headers = this.getAuthHeaders();
    const response = await fetch(
      `${this.config.baseUrl}/ratings/${targetType}/${targetId}/me`,
      { headers }
    );
    if (response.status === 404) return null;
    if (!response.ok) return null;
    return handleResponse(response);
  }

  async submitRating(
    targetType: RatingTargetType,
    targetId: string,
    data: RatingSubmitData
  ): Promise<Rating> {
    const headers = this.getAuthHeaders();
    const response = await fetch(
      `${this.config.baseUrl}/ratings/${targetType}/${targetId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  }

  async deleteRating(targetType: RatingTargetType, targetId: string): Promise<void> {
    const headers = this.getAuthHeaders();
    const response = await fetch(
      `${this.config.baseUrl}/ratings/${targetType}/${targetId}`,
      { method: 'DELETE', headers }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to delete rating');
    }
  }
}
